import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Calendar, Phone, Mail, MapPin, Car, Utensils, Compass, Star, ArrowRight, Home } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { DEFAULT_HOURS } from '../i18n/intros';

const BookingConfirmation = () => {
  const { hotelInfo, gallery } = useHotel();
  const heroPhoto = (gallery || []).find((g) => g.category === 'Sea View Rooms') || (gallery || [])[0];
  const [searchParams] = useSearchParams();
  
  const checkIn = searchParams.get('checkin') || '';
  const checkOut = searchParams.get('checkout') || '';
  const roomName = searchParams.get('room') || 'Your Room';

  const upsellServices = [
    {
      icon: Car,
      title: "Airport Transfer",
      description: "Comfortable private transfer from Tivat or Podgorica airport",
      price: "From €45",
      color: "blue"
    },
    {
      icon: Utensils,
      title: "Restaurant Reservations",
      description: "Book a table at our on-site restaurant with sea view dining",
      price: "Complimentary",
      color: "orange"
    },
    {
      icon: Compass,
      title: "Local Tours & Excursions",
      description: "Explore Ulcinj Old Town, Ada Bojana, or Lake Skadar",
      price: "From €25/person",
      color: "green"
    }
  ];

  const hrs = { ...DEFAULT_HOURS, ...(hotelInfo.hours_active || hotelInfo.hours || {}) };
  const nextSteps = [
    "Confirmation email sent to your inbox",
    `Check-in time: from ${hrs.check_in}${hrs.season ? ` (${hrs.season} hours)` : ''}`,
    `Check-out time: until ${hrs.check_out}`,
    `Breakfast: ${hrs.breakfast_from} – ${hrs.breakfast_to}`,
    "Free parking available on-site",
    "Airport shuttle can be arranged"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-gray-50">
      {/* Success Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle size={48} className="text-green-500" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Booking Confirmed!</h1>
          <p className="text-green-100 text-lg max-w-xl mx-auto">
            Thank you for choosing Hotel La Rosa Negra. We're excited to welcome you!
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Booking Summary Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8 -mt-16 relative z-10">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-1/3">
                <img 
                  src={heroPhoto?.url || '/logo.png'}
                  alt={(heroPhoto?.caption_translations || {}).en || 'Hotel La Rosa Negra'}
                  className="w-full h-48 md:h-full object-cover rounded-xl"
                />
              </div>
              <div className="md:w-2/3">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src="/logo.png"
                    alt="La Rosa Negra"
                    className="w-12 h-12 rounded-lg object-cover shadow"
                  />
                  <div>
                    <h2 className="font-bold text-xl text-gray-900">{hotelInfo.name}</h2>
                    <div className="flex items-center gap-1">
                      {[...Array(3)].map((_, i) => (
                        <Star key={i} size={14} className="text-orange-400 fill-orange-400" />
                      ))}
                      <span className="text-gray-500 text-sm ml-1">Ulcinj, Montenegro</span>
                    </div>
                  </div>
                </div>

                {checkIn && checkOut && (
                  <div className="flex items-center gap-4 mb-4 p-4 bg-gray-50 rounded-xl">
                    <Calendar className="text-orange-500" size={24} />
                    <div>
                      <p className="font-semibold text-gray-900">
                        {new Date(checkIn).toLocaleDateString('en', { weekday: 'short', month: 'long', day: 'numeric' })}
                        {' → '}
                        {new Date(checkOut).toLocaleDateString('en', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                      {roomName && <p className="text-gray-600 text-sm">{roomName}</p>}
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-2 text-gray-600 mb-4">
                  <MapPin size={18} className="text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{hotelInfo.address}</span>
                </div>

                <div className="flex flex-wrap gap-3">
                  <a 
                    href={`tel:${hotelInfo.phone}`}
                    className="flex items-center gap-2 bg-orange-50 text-orange-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-100 transition-colors"
                  >
                    <Phone size={16} />
                    {hotelInfo.phone}
                  </a>
                  <a 
                    href={`https://wa.me/${hotelInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                  <a 
                    href={hotelInfo.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                  >
                    <MapPin size={16} />
                    Get Directions
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* What's Next */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="text-green-500" size={24} />
              What's Next?
            </h3>
            <ul className="space-y-3">
              {nextSteps.map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {index + 1}
                  </span>
                  <span className="text-gray-700">{step}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Upsell Services */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
              Enhance Your Stay
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {upsellServices.map((service, index) => {
                const Icon = service.icon;
                const colorClasses = {
                  blue: 'bg-blue-50 text-blue-600',
                  orange: 'bg-orange-50 text-orange-600',
                  green: 'bg-green-50 text-green-600'
                };
                return (
                  <div key={index} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${colorClasses[service.color]}`}>
                      <Icon size={24} />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">{service.title}</h4>
                    <p className="text-gray-600 text-sm mb-3">{service.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-orange-600 font-semibold">{service.price}</span>
                      <a 
                        href={`https://wa.me/${hotelInfo.whatsapp}?text=Hi, I'd like to inquire about ${service.title} for my upcoming stay.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-500 hover:text-orange-600 flex items-center gap-1 text-sm font-medium"
                      >
                        Inquire <ArrowRight size={14} />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Return Home */}
          <div className="text-center">
            <Link 
              to="/"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg"
              data-testid="return-home-btn"
            >
              <Home size={20} />
              Return to Homepage
            </Link>
            <p className="text-gray-500 text-sm mt-4">
              Questions? Contact us at <a href={`mailto:${hotelInfo.email}`} className="text-orange-500 hover:underline">{hotelInfo.email}</a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src="/logo.png"
              alt="La Rosa Negra"
              className="w-10 h-10 rounded-lg object-cover shadow"
            />
            <span className="font-bold text-lg">{hotelInfo.name}</span>
          </div>
          <p className="text-gray-400 text-sm">{hotelInfo.location}</p>
          <p className="text-gray-500 text-xs mt-4">© {new Date().getFullYear()} {hotelInfo.name}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BookingConfirmation;
